import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, ArrowRight, CheckCircle2, BookOpen, DollarSign, Users, Calendar, Pen, ChevronRight, Sparkles, Target } from 'lucide-react';
import { Button } from '../components/ui/button';
import { MarketingNav } from '../components/MarketingLayout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GRADE_CONFIG = {
  freshman: { label: 'Freshman', color: '#2563eb', bgClass: 'bg-blue-50 border-blue-200 text-blue-700' },
  sophomore: { label: 'Sophomore', color: '#7c3aed', bgClass: 'bg-purple-50 border-purple-200 text-purple-700' },
  junior: { label: 'Junior', color: '#D4AF37', bgClass: 'bg-amber-50 border-amber-200 text-amber-700' },
  senior: { label: 'Senior', color: '#0F392B', bgClass: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
};

const SECTION_ICONS = {
  academics: BookOpen,
  activities: Users,
  planning: Target,
  financial: DollarSign,
  reading: BookOpen,
  fall: Calendar,
  winter: Calendar,
  spring_summer: Calendar,
  spring: Calendar,
  summer: Calendar,
};

function ChecklistSection({ title, items, icon: Icon, color }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4" style={{ color }} />}
        <h3 className="text-sm font-semibold capitalize">{title.replace('_', ' ')}</h3>
      </div>
      <div className="space-y-1.5 pl-6">
        {items.map((item, i) => (
          <div key={i} className="flex items-start gap-2.5">
            <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-emerald-400" />
            <p className="text-xs text-muted-foreground leading-relaxed">{item}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function EssayTipsSection({ tips }) {
  if (!tips) return null;
  return (
    <div className="space-y-6" data-testid="essay-tips-section">
      <div className="text-center">
        <Badge className="bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/20 text-xs mb-3">From the Vault College Readiness Guide</Badge>
        <h2 className="text-xl font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>{tips.title}</h2>
        <p className="text-sm text-muted-foreground mt-1 max-w-lg mx-auto">{tips.intro}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="border-emerald-200/60">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold text-emerald-700 mb-3 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" /> Do's
            </h3>
            <div className="space-y-2">
              {tips.dos.map((tip, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="text-emerald-400 text-xs mt-0.5">+</span>
                  <p className="text-xs text-muted-foreground">{tip}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border-red-200/60">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold text-red-600 mb-3 flex items-center gap-2">
              <Pen className="h-4 w-4" /> Don'ts
            </h3>
            <div className="space-y-2">
              {tips.donts.map((tip, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="text-red-400 text-xs mt-0.5">-</span>
                  <p className="text-xs text-muted-foreground">{tip}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-[#E2E2E2]">
        <CardContent className="p-5">
          <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[#D4AF37]" /> The Rules
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {tips.rules.map((rule, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="text-xs font-bold text-[#0F392B] mt-0.5">{i + 1}.</span>
                <p className="text-xs text-muted-foreground">{rule}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-[#D4AF37]/20 bg-[#D4AF37]/[0.02]">
        <CardContent className="p-5">
          <h3 className="text-sm font-semibold mb-2">Statement of Purpose Strategy</h3>
          <p className="text-xs text-muted-foreground leading-relaxed">{tips.statement_of_purpose}</p>
        </CardContent>
      </Card>
    </div>
  );
}

export default function CollegeReadinessPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeGrade, setActiveGrade] = useState('freshman');
  const [showEssayTips, setShowEssayTips] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/college-readiness`)
      .then(r => r.json())
      .then(setData)
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const gradeData = data?.[activeGrade];
  const essayData = data?.essay_tips;
  const config = GRADE_CONFIG[activeGrade];

  if (loading) return (
    <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#0F392B] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F9F9F7] pt-16" data-testid="college-readiness-page">
      {/* Nav */}
      <MarketingNav />

      {/* Hero */}
      <section className="bg-[#0F392B] text-white py-14 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30 mb-4 text-xs">Based on Vault College Readiness Guides</Badge>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Your Grade-by-Grade<br />College Prep Roadmap
          </h1>
          <p className="text-base text-white/70 max-w-xl mx-auto leading-relaxed">
            Everything you should be doing right now — academics, activities, planning, and finances — organized by your current year in high school.
          </p>
        </div>
      </section>

      {/* Grade Tabs */}
      <div className="max-w-4xl mx-auto px-4 -mt-5">
        <div className="bg-white rounded-xl border border-[#E2E2E2] shadow-lg p-1.5 flex gap-1" data-testid="grade-tabs">
          {Object.entries(GRADE_CONFIG).map(([key, cfg]) => (
            <button key={key} onClick={() => { setActiveGrade(key); setShowEssayTips(false); }}
              className={`flex-1 py-2.5 px-4 rounded-lg text-xs font-semibold transition-all ${activeGrade === key ? 'text-white shadow-md' : 'text-muted-foreground hover:text-foreground hover:bg-[#F9F9F7]'}`}
              style={activeGrade === key ? { backgroundColor: cfg.color } : {}}
              data-testid={`tab-${key}`}>
              {cfg.label}
            </button>
          ))}
          <button onClick={() => setShowEssayTips(true)}
            className={`flex-1 py-2.5 px-4 rounded-lg text-xs font-semibold transition-all ${showEssayTips ? 'bg-[#D4AF37] text-white shadow-md' : 'text-muted-foreground hover:text-foreground hover:bg-[#F9F9F7]'}`}
            data-testid="tab-essay-tips">
            Essay Tips
          </button>
        </div>
      </div>

      {/* Content */}
      <section className="max-w-4xl mx-auto px-4 py-10">
        {showEssayTips ? (
          <EssayTipsSection tips={essayData} />
        ) : gradeData && (
          <div className="space-y-6" data-testid={`grade-content-${activeGrade}`}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: config.color }}>
                <GraduationCap className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {gradeData.grade}
                </h2>
                <p className="text-xs text-muted-foreground">{gradeData.theme}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {Object.entries(gradeData).filter(([k]) => !['grade', 'theme'].includes(k) && Array.isArray(gradeData[k])).map(([key, items]) => (
                <Card key={key} className="border-[#E2E2E2]">
                  <CardContent className="p-5">
                    <ChecklistSection
                      title={key}
                      items={items}
                      icon={SECTION_ICONS[key] || Calendar}
                      color={config.color}
                    />
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <Card className="border-[#0F392B]/20 bg-[#0F392B]/[0.02] mt-10">
          <CardContent className="p-8 text-center">
            <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Ready to Organize Your Application?
            </h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
              Our portal mirrors the Common App workflow — track activities, write essays with AI feedback, manage deadlines, and get a submission-ready checklist.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/sign-up">
                <Button className="bg-[#0F392B] hover:bg-[#0F392B]/90 text-white h-10 px-6 text-sm font-semibold">
                  Start Your Application <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
              <Link to="/career-insights">
                <Button variant="outline" className="h-10 px-6 text-sm">
                  Explore Career Paths
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#E2E2E2] bg-white py-8">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GraduationCap className="h-4 w-4 text-[#0F392B]" strokeWidth={1.5} />
            <span className="text-xs text-muted-foreground">Ivy League Consulting</span>
          </div>
          <p className="text-[10px] text-muted-foreground">Content sourced from Vault College Readiness Guides</p>
        </div>
      </footer>
    </div>
  );
}
